import { Button, colors } from '@steelwake/ui';
import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';

import { API_URL, api, describeError } from '../api.js';
import { flushSettingsSync } from '../settings/sync.js';
import { useAppStore } from '../store.js';
import { Card, Field, Notice, PageTitle } from '../ui.js';

const DELETE_WORD = 'DELETE';

export function Account() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = useAppStore((s) => s.user);
  const authStatus = useAppStore((s) => s.authStatus);
  const logout = useAppStore((s) => s.logout);
  const setUser = useAppStore((s) => s.setUser);
  const [notice, setNotice] = useState<string | null>(
    params.get('oauth') === 'ok' ? 'Signed in with your provider account.' : null,
  );
  const [error, setError] = useState<string | null>(null);
  // Set before signing out so the redirect-to-login below does not race the navigation home.
  const [leaving, setLeaving] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwBusy, setPwBusy] = useState(false);
  const [del, setDel] = useState({ password: '', totpCode: '', word: '' });
  const [delBusy, setDelBusy] = useState(false);

  if (deleted) {
    return (
      <Card>
        <PageTitle>Account deleted</PageTitle>
        <Notice kind="success" testId="account-deleted">
          Your account has been deleted. Your match records stay under an anonymous name.
        </Notice>
        <p>
          <Link to="/">Back to the home page</Link>
        </p>
      </Card>
    );
  }
  if (authStatus === 'unknown') {
    return (
      <Card>
        <Notice kind="info">Loading…</Notice>
      </Card>
    );
  }
  if (!user && !leaving) return <Navigate to="/login" replace />;
  if (!user) return null;

  async function resend() {
    try {
      const res = await api<{ message: string }>('/api/v1/auth/resend-verification', {
        method: 'POST',
      });
      setNotice(res.message);
    } catch (err) {
      setError(describeError(err));
    }
  }

  async function signOut() {
    setLeaving(true);
    await logout();
    await navigate('/');
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (pw.next !== pw.confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    setPwBusy(true);
    try {
      await api('/api/v1/auth/change-password', {
        method: 'POST',
        body: { currentPassword: pw.current, newPassword: pw.next },
      });
      setPw({ current: '', next: '', confirm: '' });
      setNotice('Password changed. Every other session was signed out.');
      // The temporary-password warning goes once the account no longer carries the expiry.
      const me = await api<{ user: typeof user }>('/api/v1/auth/me');
      setUser(me.user);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setPwBusy(false);
    }
  }

  async function deleteAccount(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (del.word !== DELETE_WORD) {
      setError(`Type ${DELETE_WORD} to confirm.`);
      return;
    }
    setDelBusy(true);
    try {
      // Anything waiting on the settings debounce goes now; nothing may write after the account.
      await flushSettingsSync();
      await api('/api/v1/auth/reauth', {
        method: 'POST',
        body: { password: del.password, ...(del.totpCode ? { totpCode: del.totpCode } : {}) },
      });
      await api('/api/v1/profile', { method: 'DELETE', body: { confirm: DELETE_WORD } });
      setLeaving(true);
      setDeleted(true);
      setUser(null);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setDelBusy(false);
    }
  }

  return (
    <Card>
      <PageTitle>Your account</PageTitle>
      <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 16px' }}>
        <dt>Email</dt>
        <dd data-testid="account-email">{user.email}</dd>
        <dt>Callsign</dt>
        <dd data-testid="account-callsign">{user.callsign ?? '—'}</dd>
        <dt>Verified</dt>
        <dd
          data-testid="account-verified"
          style={{ color: user.emailVerified ? colors.success : colors.warning }}
        >
          {user.emailVerified ? 'yes' : 'not yet'}
        </dd>
        {user.staffRole ? (
          <>
            <dt>Staff role</dt>
            <dd>{user.staffRole}</dd>
          </>
        ) : null}
      </dl>
      {user.passwordExpiresAt ? (
        <Notice kind="info" testId="account-temporary-password">
          You signed in with a temporary password that expires at{' '}
          {new Date(user.passwordExpiresAt).toLocaleString()}. Change it below now.
        </Notice>
      ) : null}
      {notice ? (
        <Notice kind="success" testId="account-notice">
          {notice}
        </Notice>
      ) : null}
      {error ? (
        <Notice kind="error" testId="form-error">
          {error}
        </Notice>
      ) : null}
      <div style={{ display: 'flex', gap: 8 }}>
        {!user.emailVerified ? (
          <Button variant="ghost" onClick={resend}>
            Resend verification email
          </Button>
        ) : null}
        <Button onClick={signOut} data-testid="logout">
          Log out
        </Button>
      </div>

      <h2 style={{ fontSize: 16, marginTop: 32 }}>Change password</h2>
      <form onSubmit={changePassword} noValidate data-testid="change-password-form">
        <Field
          label="Current password"
          name="currentPassword"
          type="password"
          autoComplete="current-password"
          required
          value={pw.current}
          onChange={(e) => setPw({ ...pw, current: e.target.value })}
        />
        <Field
          label="New password"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 10 characters; not among the most common leaked passwords"
          value={pw.next}
          onChange={(e) => setPw({ ...pw, next: e.target.value })}
        />
        <Field
          label="Confirm new password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          value={pw.confirm}
          onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
        />
        <Button type="submit" disabled={pwBusy} data-testid="change-password-submit">
          {pwBusy ? 'Saving…' : 'Change password'}
        </Button>
      </form>

      <h2 style={{ fontSize: 16, marginTop: 32 }}>Your data</h2>
      <p style={{ color: colors.textMuted }}>
        Everything this account holds (profile, balances, ledger, match history, loadouts and
        settings) as one JSON file.
      </p>
      <p>
        <a href={`${API_URL}/api/v1/profile/export`} download data-testid="export-link">
          Download my data
        </a>
      </p>

      <h2 style={{ fontSize: 16, marginTop: 32 }}>Delete account</h2>
      <p style={{ color: colors.textMuted }}>
        Deletion is immediate and cannot be undone: your email, callsign, password, settings and
        hangar are removed. Match records and the credits ledger are kept under an anonymous name.
      </p>
      <form onSubmit={deleteAccount} noValidate data-testid="delete-account-form">
        <Field
          label="Password"
          name="deletePassword"
          type="password"
          autoComplete="current-password"
          required
          value={del.password}
          onChange={(e) => setDel({ ...del, password: e.target.value })}
        />
        {user.totpEnrolled ? (
          <Field
            label="Authenticator code"
            name="deleteTotp"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={del.totpCode}
            onChange={(e) => setDel({ ...del, totpCode: e.target.value })}
          />
        ) : null}
        <Field
          label={`Type ${DELETE_WORD} to confirm`}
          name="deleteWord"
          type="text"
          autoComplete="off"
          required
          value={del.word}
          onChange={(e) => setDel({ ...del, word: e.target.value })}
        />
        <Button
          type="submit"
          variant="ghost"
          disabled={delBusy || del.word !== DELETE_WORD || del.password.length === 0}
          data-testid="delete-account-submit"
        >
          {delBusy ? 'Deleting…' : 'Delete my account'}
        </Button>
      </form>
    </Card>
  );
}
